import { useState, useEffect } from 'react';

export default function Home() {
  const [formStatus, setFormStatus] = useState<string | null>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": `${siteUrl}/#business`,
          "name": "Luxe Auto Financial",
          "description": "Luxe Auto Financial offers expert car buying negotiation, auction brokerage (Copart/IAAI), vehicle sourcing, and consultation services in Matawan, NJ. We help NJ buyers buy smarter and avoid overpaying.",
          "url": siteUrl,
          "telephone": "+17324514377",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Matawan",
            "addressRegion": "NJ",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 40.4168,
            "longitude": -74.2257
          },
          "areaServed": {
            "@type": "State",
            "name": "New Jersey"
          },
          "priceRange": "$$",
          "sameAs": []
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": siteUrl,
          "name": "Matawan NJ Car Buying Help & Auction Brokerage | Luxe Auto Financial",
          "description": "Luxe Auto Financial in Matawan, NJ offers expert car buying negotiation, auction brokerage (Copart/IAAI), and vehicle sourcing. Save time, avoid overpaying, and buy smarter.",
          "isPartOf": { "@id": `${siteUrl}/#business` },
          "inLanguage": "en-US"
        },
        {
          "@type": "Service",
          "serviceType": "Car Buying Negotiation",
          "provider": { "@id": `${siteUrl}/#business` },
          "areaServed": { "@type": "State", "name": "New Jersey" },
          "description": "We help negotiate better pricing and terms so you do not overpay at the dealership."
        },
        {
          "@type": "Service",
          "serviceType": "Auction Brokerage",
          "provider": { "@id": `${siteUrl}/#business` },
          "areaServed": { "@type": "State", "name": "New Jersey" },
          "description": "We help you buy through auctions like Copart or IAAI — understand the risks, evaluate options, and buy smarter."
        },
        {
          "@type": "Service",
          "serviceType": "Vehicle Sourcing",
          "provider": { "@id": `${siteUrl}/#business` },
          "areaServed": { "@type": "State", "name": "New Jersey" },
          "description": "We help locate vehicles that match your budget, needs, and preferences."
        },
        {
          "@type": "Service",
          "serviceType": "Consultation Services",
          "provider": { "@id": `${siteUrl}/#business` },
          "areaServed": { "@type": "State", "name": "New Jersey" },
          "description": "We review your situation and help you choose the best car-buying path forward."
        }
      ]
    };

    const existing = document.getElementById('schema-jsonld');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = 'schema-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="flex flex-col font-sans">

      {/* ── Top Navigation Bar ── */}
      <header className="w-full px-6 py-3 flex items-center justify-between sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(15,15,15,0.96)', backdropFilter: 'blur(10px)' }}>
        {/* Text Logo */}
        <a href="/" className="flex items-center gap-0 cursor-pointer select-none group">
          <span
            className="font-black leading-none tracking-tight"
            style={{
              fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
              fontSize: '1.65rem',
              background: 'linear-gradient(135deg, #b8922a 0%, #e8c96a 45%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontStyle: 'italic',
            }}
          >
            Luxe
          </span>
          <span
            className="leading-none text-white/90 ml-2.5"
            style={{
              fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif",
              fontSize: '0.62rem',
              fontWeight: 300,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              alignSelf: 'center',
              marginTop: '2px',
            }}
          >
            AUTO FINANCIAL
          </span>
        </a>
        {/* Nav links */}
        <nav className="flex items-center">
          <a
            href="tel:+17324514377"
            onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion('tel:+17324514377'); }}
            className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white uppercase tracking-widest transition-colors cursor-pointer"
          >
            <i className="ri-phone-line text-amber-400"></i>
            (732) 451-4377
          </a>
        </nav>
      </header>

      {/* ── Hero Section ── dark charcoal */}
      <div
        className="relative flex flex-col items-center justify-center min-h-[52vh] text-center px-6 py-16"
        style={{ background: 'linear-gradient(135deg, #0f0f0f 0%, #1c1c1c 60%, #111 100%)' }}
      >
        {/* subtle texture overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }}></div>
        {/* gold accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)' }}></div>

        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
            <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Helping Buyers Buy Smarter</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white tracking-tight">
            Car Buying Help &amp; Auction Brokerage<br className="hidden md:block" /> in New Jersey
          </h1>
          <p className="mt-5 text-base md:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Luxe Auto Financial helps you buy smarter with vehicle sourcing, dealership negotiation, and auction brokerage designed to save you time, reduce stress, and help you avoid overpaying.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#contact-form" className="whitespace-nowrap px-7 py-3.5 font-semibold rounded-md transition-all cursor-pointer text-sm tracking-wide" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)', color: '#0f0f0f' }}>
              Get Started
            </a>
            <a href="#contact-form" className="whitespace-nowrap px-7 py-3.5 border border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors cursor-pointer text-sm tracking-wide">
              Request a Consultation
            </a>
            <a
              href="tel:+17324514377"
              onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion('tel:+17324514377'); }}
              className="whitespace-nowrap px-5 py-3.5 text-gray-400 font-semibold rounded-md hover:text-white transition-colors cursor-pointer text-sm flex items-center gap-2"
            >
              <i className="ri-phone-line text-base"></i>
              Call Now
            </a>
          </div>
        </div>
      </div>

      {/* ── About / Intro Section ── soft off-white */}
      <section className="py-14 px-6" style={{ background: '#f7f6f3' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-900">
            Expert help for buyers who want a better car deal
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-500 leading-relaxed max-w-3xl mx-auto">
            Whether you're buying from a dealership, searching for the right vehicle, or considering an auction purchase, Luxe Auto Financial helps guide the process from start to finish. We work to simplify the car-buying experience, reduce stress, and help you make a confident decision.
          </p>
        </div>
      </section>

      {/* ── Services Section ── white */}
      <section className="py-14 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-bold tracking-widest text-amber-600 uppercase mb-2">What We Offer</p>
          <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-900">Our Services</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 text-left">
            {[
              { icon: 'ri-price-tag-3-line', title: 'Car Buying Negotiation', desc: 'We help negotiate better pricing and terms so you do not overpay at the dealership.' },
              { icon: 'ri-auction-line', title: 'Auction Brokerage', desc: 'Need help buying through auctions like Copart or IAAI? We help you understand the risks, evaluate options, and buy smarter.' },
              { icon: 'ri-search-line', title: 'Vehicle Sourcing', desc: 'Looking for a specific vehicle? We help locate options that match your budget, needs, and preferences.' },
              { icon: 'ri-customer-service-2-line', title: 'Consultation Services', desc: 'Not sure where to start? We can review your situation and help you choose the best path forward.' },
            ].map((s) => (
              <div key={s.title} className="rounded-xl p-7 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all" style={{ background: '#fafaf8' }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-md mb-4" style={{ background: 'linear-gradient(135deg, #1a1a1a, #333)' }}>
                  <i className={`${s.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── dark charcoal */}
      <section className="py-14 px-6" style={{ background: '#141414' }}>
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-bold tracking-widest text-amber-500 uppercase mb-2">Our Advantage</p>
          <h2 className="text-3xl md:text-4xl font-bold leading-tight text-white">Why Choose Luxe Auto Financial</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 text-left">
            {[
              { icon: 'ri-time-line', title: 'Save Time', desc: 'We do the legwork so you can focus on the right vehicle instead of wasting time.' },
              { icon: 'ri-shield-check-line', title: 'Avoid Costly Mistakes', desc: 'From pricing to auction risks, we help you make informed decisions.' },
              { icon: 'ri-user-heart-line', title: 'Personalized Guidance', desc: "Every buyer's situation is different. We tailor the process to your needs." },
              { icon: 'ri-map-pin-line', title: 'Local Expertise', desc: 'Based in New Jersey, we understand the local market and how to help buyers navigate it.' },
            ].map((w) => (
              <div key={w.title} className="rounded-xl p-7 border border-white/10 hover:border-white/20 transition-all" style={{ background: '#1e1e1e' }}>
                <div className="w-11 h-11 flex items-center justify-center rounded-md mb-4" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)' }}>
                  <i className={`${w.icon} text-gray-900 text-lg`}></i>
                </div>
                <h3 className="text-base font-bold text-white mb-2">{w.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── soft off-white */}
      <section className="py-14 px-6" style={{ background: '#f7f6f3' }}>
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-bold tracking-widest text-amber-600 uppercase mb-2">Simple Process</p>
          <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-900">How It Works</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { n: '1', title: 'Tell Us What You Need', desc: 'Let us know what type of vehicle you want, your budget, and whether you need negotiation or auction help.' },
              { n: '2', title: 'We Build a Strategy', desc: 'We review options, identify opportunities, and help map out the best approach.' },
              { n: '3', title: 'We Help You Buy Smarter', desc: 'From sourcing to negotiation to auction guidance, we help you move forward with confidence.' },
            ].map((step) => (
              <div key={step.n} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 flex items-center justify-center rounded-full mb-5" style={{ background: '#141414' }}>
                  <span className="text-white font-bold text-sm">{step.n}</span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vehicle Sourcing CTA ── white with dark accent border */}
      <section className="py-14 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="rounded-2xl px-10 py-12 border border-gray-200 shadow-sm" style={{ background: '#fafaf8' }}>
            <p className="text-xs font-bold tracking-widest text-amber-600 uppercase mb-3">Vehicle Sourcing</p>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-900">
              Need Help Finding the Right Car?
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-500 leading-relaxed">
              Even if we do not have your ideal vehicle in current inventory, we can help source the right car based on your budget, needs, and preferences.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#contact-form"
                className="whitespace-nowrap px-7 py-3.5 font-semibold rounded-md transition-all cursor-pointer text-sm tracking-wide"
                style={{ background: '#141414', color: '#fff' }}
              >
                Need Help Finding a Car?
              </a>
              <a
                href="https://www.eurohubllc.com/apply-online/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap px-7 py-3.5 border-2 border-gray-900 text-gray-900 font-semibold rounded-md hover:bg-gray-900 hover:text-white transition-colors cursor-pointer text-sm tracking-wide"
              >
                Apply for Financing (Current Inventory)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact Form Section ── deep charcoal */}
      <section id="contact-form" className="py-16 px-6" style={{ background: 'linear-gradient(160deg, #0f0f0f 0%, #1a1a1a 100%)' }}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-amber-500 uppercase mb-3">Get In Touch</p>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight text-white">Ready to buy smarter? Let&apos;s talk.</h2>
            <p className="mt-3 text-base text-gray-400 max-w-xl mx-auto leading-relaxed">
              Tell us what you&apos;re looking for and we&apos;ll respond quickly with next steps — no pressure, just expert guidance.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5">
              <i className="ri-map-pin-2-line text-amber-400 text-sm"></i>
              <p className="text-sm font-medium text-gray-300 tracking-wide">
                Based in Matawan, NJ &bull; Personalized vehicle sourcing, auction brokerage, and car buying negotiation support.
              </p>
            </div>
            <p className="mt-2 text-xs text-gray-500 tracking-wide">Serving NJ buyers statewide.</p>
          </div>

          <form
            id="contact-form-el"
            data-readdy-form
            action="https://readdy.ai/api/form/d6h1e1b5pk3jcsh37ftg"
            method="POST"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const data = new URLSearchParams(new FormData(form) as any);
              try {
                const res = await fetch("https://readdy.ai/api/form/d6h1e1b5pk3jcsh37ftg", {
                  method: "POST",
                  headers: { "Content-Type": "application/x-www-form-urlencoded" },
                  body: data.toString(),
                });
                if (res.ok) {
                  // Fire Google Ads conversion event on successful form submission
                  if (typeof (window as any).gtag === 'function') {
                    (window as any).gtag('event', 'conversion', {
                      send_to: 'AW-17981724340/9q3lCO-JoIAcELStrf5C',
                      value: 1.0,
                      currency: 'USD',
                    });
                  }
                  setFormStatus("success");
                  form.reset();
                } else {
                  setFormStatus("error");
                }
              } catch { setFormStatus("error"); }
            }}
            className="rounded-2xl p-8 border border-white/10 space-y-4"
            style={{ background: '#1e1e1e' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">First Name</label>
                <input type="text" name="first_name" required placeholder="John"
                  className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white placeholder-gray-600"
                  style={{ background: '#2a2a2a', border: '1px solid #333' }}
                  onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                  onBlur={e => (e.target.style.borderColor = '#333')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">Last Name</label>
                <input type="text" name="last_name" required placeholder="Doe"
                  className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white placeholder-gray-600"
                  style={{ background: '#2a2a2a', border: '1px solid #333' }}
                  onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                  onBlur={e => (e.target.style.borderColor = '#333')}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">Email Address</label>
              <input type="email" name="email" required placeholder="john@example.com"
                className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white placeholder-gray-600"
                style={{ background: '#2a2a2a', border: '1px solid #333' }}
                onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                onBlur={e => (e.target.style.borderColor = '#333')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">Phone Number</label>
              <input type="tel" name="phone" placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white placeholder-gray-600"
                style={{ background: '#2a2a2a', border: '1px solid #333' }}
                onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                onBlur={e => (e.target.style.borderColor = '#333')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">Service Needed</label>
              <select name="service" required
                className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white cursor-pointer"
                style={{ background: '#2a2a2a', border: '1px solid #333' }}
                onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                onBlur={e => (e.target.style.borderColor = '#333')}
              >
                <option value="" style={{ background: '#2a2a2a' }}>Select a service...</option>
                <option value="Deal Negotiation (Dealership)" style={{ background: '#2a2a2a' }}>Deal Negotiation (Dealership)</option>
                <option value="Auction Help (Copart/IAAI)" style={{ background: '#2a2a2a' }}>Auction Help (Copart/IAAI)</option>
                <option value="Vehicle Sourcing (Find Me a Car)" style={{ background: '#2a2a2a' }}>Vehicle Sourcing (Find Me a Car)</option>
                <option value="Consultation / Questions" style={{ background: '#2a2a2a' }}>Consultation / Questions</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 tracking-wide uppercase">Message</label>
              <textarea name="message" rows={4} maxLength={500}
                placeholder="Tell us more about what you're looking for..."
                className="w-full px-4 py-3 text-sm rounded-md focus:outline-none transition-colors text-white placeholder-gray-600 resize-none"
                style={{ background: '#2a2a2a', border: '1px solid #333' }}
                onFocus={e => (e.target.style.borderColor = '#c9a84c')}
                onBlur={e => (e.target.style.borderColor = '#333')}
              />
              <p className="text-xs text-gray-600 mt-1">Max 500 characters</p>
            </div>
            {formStatus === "success" && (
              <div className="p-3 rounded-md text-sm font-medium" style={{ background: '#1a2e1a', border: '1px solid #2d5a2d', color: '#6fcf6f' }}>
                Thank you! Your message has been sent. We&apos;ll be in touch shortly.
              </div>
            )}
            {formStatus === "error" && (
              <div className="p-3 rounded-md text-sm font-medium" style={{ background: '#2e1a1a', border: '1px solid #5a2d2d', color: '#cf6f6f' }}>
                Something went wrong. Please try again.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              <button type="submit"
                className="whitespace-nowrap w-full px-6 py-3.5 font-semibold rounded-md transition-all cursor-pointer text-sm tracking-wide"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)', color: '#0f0f0f' }}
              >
                Request a Consultation
              </button>
              <a
                href="tel:+17324514377"
                onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion('tel:+17324514377'); }}
                className="whitespace-nowrap w-full px-6 py-3.5 border border-white/20 text-white font-semibold rounded-md hover:bg-white/10 transition-colors cursor-pointer text-sm text-center flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line text-base"></i>
                Call Now
              </a>
            </div>
          </form>
        </div>
      </section>

      {/* ── Footer ── charcoal with prominent logo */}
      <footer className="px-6 pt-12 pb-6 border-t border-white/10" style={{ background: '#0a0a0a' }}>
        <div className="max-w-5xl mx-auto">
          {/* Brand text + tagline */}
          <div className="flex flex-col items-center text-center mb-8">
            <a href="/" className="flex flex-col items-center cursor-pointer select-none mb-3 group">
              <span
                className="font-black leading-none tracking-tight"
                style={{
                  fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                  fontSize: '3.2rem',
                  background: 'linear-gradient(135deg, #b8922a 0%, #f0d878 45%, #c9a84c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontStyle: 'italic',
                  filter: 'drop-shadow(0 0 18px rgba(201,168,76,0.5))',
                  lineHeight: 1,
                }}
              >
                Luxe
              </span>
              <span
                className="leading-none text-white/70 mt-1"
                style={{
                  fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif",
                  fontSize: '0.68rem',
                  fontWeight: 300,
                  letterSpacing: '0.42em',
                  textTransform: 'uppercase',
                }}
              >
                AUTO FINANCIAL
              </span>
            </a>
            <p className="text-xs tracking-widest text-gray-500 uppercase font-semibold">Car Buying Help &bull; Auction Brokerage &bull; Vehicle Sourcing</p>
            <p className="text-xs text-gray-600 mt-1">Matawan, NJ &mdash; Serving buyers statewide</p>
          </div>

          {/* Gold divider */}
          <div className="w-full h-px mb-6" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c55, transparent)' }}></div>

          {/* Trust bar */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-center mb-6">
            {['Straightforward help.', 'Smarter buying.', 'Less stress.'].map((text, i) => (
              <>
                {i > 0 && <span key={`sep-${i}`} className="text-white/10 hidden md:inline text-lg">|</span>}
                <span key={text} className="flex items-center gap-2 text-sm font-semibold text-gray-400">
                  <i className="ri-check-line text-amber-500 text-base"></i>
                  {text}
                </span>
              </>
            ))}
          </div>

          {/* Bottom row */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/5">
            <p className="text-xs text-gray-600">&copy; {new Date().getFullYear()} Luxe Auto Financial. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a
                href="tel:+17324514377"
                onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion('tel:+17324514377'); }}
                className="text-xs text-gray-500 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-phone-line text-xs"></i>
                (732) 451-4377
              </a>
              <a href="#contact-form" className="text-xs text-gray-500 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
